import {
  conditionLabel,
  isLeaf,
  type WorkflowEntry,
  type WorkflowEntryType,
  type WorkflowInput,
  type WorkflowStepStatus,
} from '@agent-platform/shared';
import {
  applyNodeChanges,
  Background,
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  getSmoothStepPath,
  Handle,
  MarkerType,
  MiniMap,
  NodeToolbar,
  Panel,
  Position,
  ReactFlow,
  useReactFlow,
  useStore,
  type Connection,
  type Edge,
  type EdgeProps,
  type IsValidConnection,
  type Node,
  type NodeChange,
  type NodeProps,
  type OnConnectEnd,
  type ReactFlowInstance,
  type XYPosition,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Ban,
  CheckCircle2,
  CircleDashed,
  CircleSlash,
  Clock,
  Copy,
  Filter,
  Flag,
  Hand,
  LayoutGrid,
  Loader2,
  LogIn,
  Plus,
  Trash2,
  Unlink,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useFlowColorMode } from '@/hooks/use-flow-color-mode';
import { cn } from '@/lib/utils';
import {
  CANVAS_INPUT,
  CANVAS_OUTPUT,
  chainLinks,
  connect,
  containerChildren,
  disconnect,
  duplicateEntry,
  insertAfter,
  removeChild,
  removeEntry,
  segments,
  type CanvasModel,
} from '@/lib/workflow-canvas';
import {
  CONTAINER_ENTRY_TYPES,
  ENTRY_META,
  formatSleep,
  LEAF_TYPES,
  type LiveStep,
} from '@/lib/workflow-ui';

export const STATUS_META: Record<
  WorkflowStepStatus,
  { icon: LucideIcon; label: string; className: string }
> = {
  pending: { icon: CircleDashed, label: 'Pending', className: 'text-muted-foreground' },
  running: { icon: Loader2, label: 'Running', className: 'animate-spin text-foreground' },
  succeeded: {
    icon: CheckCircle2,
    label: 'Succeeded',
    className: 'text-emerald-600 dark:text-emerald-400',
  },
  failed: { icon: XCircle, label: 'Failed', className: 'text-destructive' },
  skipped: { icon: CircleSlash, label: 'Skipped', className: 'text-muted-foreground' },
  suspended: { icon: Hand, label: 'Needs input', className: 'text-amber-600 dark:text-amber-400' },
  waiting: { icon: Clock, label: 'Sleeping', className: 'text-sky-600 dark:text-sky-400' },
  cancelled: { icon: Ban, label: 'Cancelled', className: 'text-muted-foreground' },
};

export const SELECT_INPUT = CANVAS_INPUT;
export const SELECT_OUTPUT = CANVAS_OUTPUT;

const NODE_W = 240;
const COL = 300;
const CHILD_TOP = 120;
const CHILD_ROW = 84;
const ROW_GAP = 90;

type Role = 'input' | 'output' | 'entry' | 'child';

type NodeData = {
  kind: 'input' | 'output' | WorkflowEntryType;
  role: Role;
  title: string;
  subtitle: string;
  conditional?: boolean;
  container?: boolean;
  /** Not linked to the workflow (does not run). */
  loose?: boolean;
  live?: LiveStep;
};

// --- Actions shared with nodes and edges ---

interface CanvasActions {
  editable: boolean;
  insert: (source: string, type: WorkflowEntryType) => void;
  unlink: (source: string, target: string) => void;
  remove: (id: string) => void;
  duplicate: (id: string) => void;
}
const ActionsContext = createContext<CanvasActions | null>(null);
const useActions = () => useContext(ActionsContext)!;

function StepTypeItems({ onPick }: { onPick: (type: WorkflowEntryType) => void }) {
  const item = (type: WorkflowEntryType) => {
    const meta = ENTRY_META[type];
    return (
      <DropdownMenuItem key={type} onSelect={() => onPick(type)}>
        <meta.icon aria-hidden />
        <span className="grid">
          <span>{meta.label}</span>
          <span className="text-muted-foreground text-xs">{meta.hint}</span>
        </span>
      </DropdownMenuItem>
    );
  };
  return (
    <>
      <DropdownMenuLabel>Steps</DropdownMenuLabel>
      {LEAF_TYPES.map(item)}
      <DropdownMenuSeparator />
      <DropdownMenuLabel>Control flow</DropdownMenuLabel>
      {CONTAINER_ENTRY_TYPES.map(item)}
    </>
  );
}

function progress(kind: NodeData['kind'], live: LiveStep | undefined): string | null {
  if (!live) return null;
  if (kind === 'loop' && live.iterations) return `×${live.iterations}`;
  if (kind === 'foreach' && live.items) return `${live.items.done}/${live.items.total}`;
  if (live.status === 'waiting' && live.resumeAt)
    return `until ${new Date(live.resumeAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}`;
  return null;
}

const handleClass = '!size-3 !border-2 !border-background !bg-muted-foreground';

const StepNode = memo(function StepNode({ id, data, selected }: NodeProps<Node<NodeData>>) {
  const actions = useActions();
  const Icon =
    data.kind === 'input' ? LogIn : data.kind === 'output' ? Flag : ENTRY_META[data.kind].icon;
  const status = data.live ? STATUS_META[data.live.status] : null;
  const extra = progress(data.kind, data.live);
  const editable = actions.editable;
  const deletable = data.role === 'entry' || data.role === 'child';
  return (
    <div
      data-testid="workflow-node"
      data-status={data.live?.status ?? 'idle'}
      className={cn(
        'bg-card text-card-foreground rounded-lg border px-3 py-2 shadow-xs transition-[box-shadow,border-color]',
        selected && 'ring-ring ring-2',
        data.live?.status === 'failed' && 'border-destructive',
        data.live?.status === 'running' && 'border-foreground',
        data.live?.status === 'suspended' && 'border-amber-500',
        data.live?.status === 'skipped' && 'opacity-60',
        data.container && 'border-dashed bg-[color-mix(in_oklch,var(--card),var(--foreground)_3%)]',
        data.loose && 'border-dashed border-amber-500/70',
        (data.kind === 'input' || data.kind === 'output') &&
          'bg-[color-mix(in_oklch,var(--card),var(--foreground)_5%)]',
      )}
      style={{ width: NODE_W }}
    >
      {editable && deletable && (
        <NodeToolbar position={Position.Top} className="flex gap-1">
          {data.role === 'entry' && (
            <Button
              size="icon"
              variant="outline"
              className="bg-card size-7"
              aria-label={`Duplicate ${data.title}`}
              onClick={() => actions.duplicate(id)}
            >
              <Copy />
            </Button>
          )}
          <Button
            size="icon"
            variant="outline"
            className="bg-card text-destructive hover:bg-destructive/10 hover:text-destructive size-7"
            aria-label={`Delete ${data.title}`}
            onClick={() => actions.remove(id)}
          >
            <Trash2 />
          </Button>
        </NodeToolbar>
      )}
      {data.role !== 'input' && (
        <Handle
          id="in"
          type="target"
          position={data.role === 'child' ? Position.Top : Position.Left}
          isConnectable={editable && data.role !== 'child'}
          className={handleClass}
        />
      )}
      <div className="flex items-center gap-2">
        <div className="bg-background flex size-7 shrink-0 items-center justify-center rounded-md border">
          <Icon className="size-4" aria-hidden />
        </div>
        <div className="grid min-w-0 flex-1">
          <span className="truncate text-sm font-medium">{data.title}</span>
          <span className="text-muted-foreground truncate font-mono text-[11px]">
            {data.loose ? 'not connected · does not run' : data.subtitle}
          </span>
        </div>
        {extra && (
          <span className="text-muted-foreground bg-muted shrink-0 rounded px-1 font-mono text-[10px]">
            {extra}
          </span>
        )}
        {data.conditional && (
          <Filter
            className="text-muted-foreground size-3.5 shrink-0"
            aria-label="Runs only when a condition holds"
          />
        )}
        {status && (
          <status.icon
            className={cn('size-4 shrink-0', status.className)}
            aria-label={status.label}
          />
        )}
      </div>
      {(data.role === 'entry' || data.role === 'input') && (
        <Handle
          id="out"
          type="source"
          position={Position.Right}
          isConnectable={editable}
          className={handleClass}
        />
      )}
      {data.container && (
        <Handle
          id="children"
          type="source"
          position={Position.Bottom}
          isConnectable={false}
          className="!bg-border"
        />
      )}
      {(data.kind === 'loop' || data.kind === 'foreach') && data.role === 'entry' && (
        <Handle
          id="loop-in"
          type="target"
          position={Position.Bottom}
          isConnectable={false}
          className="!border-0 !bg-transparent"
          style={{ left: '85%' }}
        />
      )}
      {data.role === 'child' && (
        <Handle
          id="back"
          type="source"
          position={Position.Right}
          isConnectable={false}
          className="!border-0 !bg-transparent"
        />
      )}
    </div>
  );
});

/** A link between two steps; on hover it offers "add a step here" and "unlink". */
function ChainEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  style,
  selected,
}: EdgeProps) {
  const actions = useActions();
  const [hover, setHover] = useState(false);
  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 12,
  });
  const show = actions.editable && (hover || selected);
  return (
    <>
      <BaseEdge id={id} path={path} markerEnd={markerEnd} style={style} />
      {/* A wide invisible path makes the link easy to hover. */}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth={24}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      />
      {actions.editable && (
        <EdgeLabelRenderer>
          <div
            className={cn(
              'nodrag nopan pointer-events-auto absolute flex gap-1 transition-opacity',
              show ? 'opacity-100' : 'opacity-0 focus-within:opacity-100',
            )}
            style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon"
                  variant="outline"
                  className="bg-card size-6 rounded-full"
                  aria-label="Add a step here"
                >
                  <Plus className="size-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-72">
                <StepTypeItems onPick={(type) => actions.insert(source, type)} />
              </DropdownMenuContent>
            </DropdownMenu>
            {target !== CANVAS_OUTPUT && (
              <Button
                size="icon"
                variant="outline"
                className="bg-card size-6 rounded-full"
                aria-label="Unlink"
                onClick={() => actions.unlink(source, target)}
              >
                <Unlink className="size-3.5" />
              </Button>
            )}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const nodeTypes = { step: StepNode };
const edgeTypes = { chain: ChainEdge };

const subtitle = (type: WorkflowEntryType, id: string) =>
  `${ENTRY_META[type].label.toLowerCase()} · ${id}`;

function entrySubtitle(entry: WorkflowEntry): string {
  switch (entry.type) {
    case 'sleep':
      return entry.mode === 'until'
        ? `until ${entry.until ?? ''}`
        : `for ${formatSleep(entry.durationMs ?? 0)}`;
    case 'loop':
      return `${entry.mode} ${conditionLabel(entry.condition, 30)}`;
    case 'foreach':
      return `each of ${entry.items} · ×${entry.concurrency}`;
    default:
      return subtitle(entry.type, entry.id);
  }
}

/** Default positions, left to right; unconnected segments get rows below. */
function autoLayout(model: CanvasModel): Map<string, XYPosition> {
  const pos = new Map<string, XYPosition>();
  const parts = segments(model);
  const rowHeight = (row: WorkflowEntry[]) =>
    Math.max(
      80,
      ...row.map((e) => (isLeaf(e) ? 80 : CHILD_TOP + containerChildren(e).length * CHILD_ROW)),
    );
  let y = 0;
  parts.forEach((row, r) => {
    const offset = r === 0 ? 1 : 0;
    if (r === 0) pos.set(CANVAS_INPUT, { x: 0, y: 0 });
    row.forEach((entry, i) => {
      const x = (i + offset) * COL;
      pos.set(entry.id, { x, y });
      containerChildren(entry).forEach((child, k) => {
        pos.set(child.id, { x: x + 24, y: y + CHILD_TOP + k * CHILD_ROW });
      });
    });
    if (r === 0) pos.set(CANVAS_OUTPUT, { x: (row.length + 1) * COL, y: 0 });
    y += rowHeight(row) + ROW_GAP;
  });
  return pos;
}

/** Positions dragged by the user, per workflow, in this browser. */
function useStoredLayout(workflowId: string) {
  const key = `workflow-layout:${workflowId}`;
  const [positions, setPositions] = useState<Record<string, XYPosition>>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as Record<string, XYPosition>) : {};
    } catch {
      return {};
    }
  });
  const save = useCallback(
    (next: Record<string, XYPosition>) => {
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Storage may be full or blocked; the layout still works for this session.
      }
    },
    [key],
  );
  return { positions, setPositions, save };
}

/** Smallest zoom that keeps node text readable; wider graphs open at their left edge. */
const MIN_FIT_ZOOM = 0.6;

/** Fits the view once per `fitKey` and canvas size (not on every edit, so editing never jumps). */
function AutoFit({ fitKey, size }: { fitKey: string; size: { w: number; h: number } }) {
  const flow = useReactFlow();
  const measured = useStore((st) => {
    let n = 0;
    st.nodeLookup.forEach((node) => {
      if (node.measured.width && node.measured.height) n++;
    });
    return n === st.nodeLookup.size ? n : 0;
  });
  const done = useRef<string | null>(null);
  useEffect(() => {
    const key = `${fitKey}:${Math.round(size.w)}x${Math.round(size.h)}`;
    if (measured === 0 || size.w === 0 || size.h === 0 || done.current === key) return;
    done.current = key;
    const bounds = flow.getNodesBounds(flow.getNodes().map((n) => n.id));
    if (bounds.width === 0 || bounds.height === 0) return;
    const pad = 40;
    const fit = Math.min(1, (size.w - 2 * pad) / bounds.width, (size.h - 2 * pad) / bounds.height);
    const zoom = Math.max(MIN_FIT_ZOOM, fit);
    const x =
      bounds.width * zoom <= size.w - 2 * pad
        ? size.w / 2 - (bounds.x + bounds.width / 2) * zoom
        : pad - bounds.x * zoom;
    const y =
      bounds.height * zoom <= size.h - 2 * pad
        ? size.h / 2 - (bounds.y + bounds.height / 2) * zoom
        : pad - bounds.y * zoom;
    void flow.setViewport({ x, y, zoom });
  }, [flow, measured, fitKey, size.w, size.h]);
  return null;
}

/** The step picker where a link was dropped on empty canvas (n8n style). */
function DropPicker({
  at,
  onPick,
  onClose,
}: {
  at: XYPosition | null;
  onPick: (type: WorkflowEntryType) => void;
  onClose: () => void;
}) {
  return (
    <DropdownMenu open={Boolean(at)} onOpenChange={(open) => !open && onClose()}>
      <DropdownMenuTrigger asChild>
        <span
          aria-hidden
          className="pointer-events-none fixed size-0"
          style={{ left: at?.x ?? 0, top: at?.y ?? 0 }}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-72" align="start">
        <StepTypeItems onPick={onPick} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The workflow canvas (n8n style). Drag nodes to arrange them; drag from a node's right handle to
 * another node to link it, or onto empty canvas to add a step there; hover a link to insert a step
 * or unlink; press Delete (or use the toolbar) to remove the selected node. Steps run in link order
 * from Input; unlinked steps stay on the canvas but do not run.
 */
export function WorkflowGraph({
  workflowId,
  model,
  inputs,
  hasOutput,
  selected,
  live,
  editable = false,
  onSelect,
  onChange,
  onCreate,
  className,
  fitKey = 'default',
}: {
  workflowId: string;
  model: CanvasModel;
  inputs: WorkflowInput[];
  hasOutput: boolean;
  selected: string | null;
  live: Record<string, LiveStep>;
  editable?: boolean;
  onSelect: (id: string) => void;
  onChange?: (model: CanvasModel) => void;
  /** Builds a new entry (with ids free in the workflow). */
  onCreate?: (type: WorkflowEntryType) => WorkflowEntry;
  className?: string;
  /** Changing it fits the view again (e.g. switching between the draft and a run). */
  fitKey?: string;
}) {
  const colorMode = useFlowColorMode();
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize((prev) =>
        Math.abs(prev.w - width) < 1 && Math.abs(prev.h - height) < 1
          ? prev
          : { w: width, h: height },
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { positions, setPositions, save } = useStoredLayout(workflowId);
  const [drop, setDrop] = useState<{ source: string; screen: XYPosition; flow: XYPosition } | null>(
    null,
  );
  const flowRef = useRef<ReactFlowInstance<Node<NodeData>> | null>(null);

  // Which container holds each child step.
  const parents = useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of model.graph)
      for (const child of containerChildren(entry)) map.set(child.id, entry.id);
    return map;
  }, [model.graph]);

  const built = useMemo(() => {
    const auto = autoLayout(model);
    const loose = new Set(
      segments(model)
        .slice(1)
        .flat()
        .map((e) => e.id),
    );
    const nodes: Node<NodeData>[] = [];
    const place = (id: string): XYPosition => {
      const stored = positions[id];
      if (stored) return stored;
      // Children follow their container until they are dragged themselves.
      const parent = parents.get(id);
      if (parent && positions[parent]) {
        const a = auto.get(parent)!;
        const c = auto.get(id)!;
        return { x: positions[parent].x + (c.x - a.x), y: positions[parent].y + (c.y - a.y) };
      }
      return auto.get(id) ?? { x: 0, y: 0 };
    };
    const add = (id: string, data: NodeData) =>
      nodes.push({
        id,
        type: 'step',
        position: place(id),
        data: { ...data, ...(live[id] ? { live: live[id] } : {}) },
        selected: selected === id,
        deletable: editable && (data.role === 'entry' || data.role === 'child'),
        connectable: editable,
      });
    add(CANVAS_INPUT, {
      kind: 'input',
      role: 'input',
      title: 'Input',
      subtitle: inputs.length ? inputs.map((i) => i.name).join(', ') : 'no inputs',
    });
    for (const entry of model.graph) {
      add(entry.id, {
        kind: entry.type,
        role: 'entry',
        title: entry.name,
        subtitle: entrySubtitle(entry),
        container: !isLeaf(entry) && entry.type !== 'sleep',
        conditional: 'when' in entry && Boolean(entry.when),
        loose: loose.has(entry.id),
      });
      for (const child of containerChildren(entry))
        add(child.id, {
          kind: child.type,
          role: 'child',
          title: child.name,
          subtitle: subtitle(child.type, child.id),
          conditional: Boolean(child.when),
          loose: loose.has(entry.id),
        });
    }
    add(CANVAS_OUTPUT, {
      kind: 'output',
      role: 'output',
      title: 'Output',
      subtitle: hasOutput ? 'custom template' : 'last step’s output',
    });

    const edges: Edge[] = [];
    const style = { strokeWidth: 1.5 };
    const labelProps = {
      labelStyle: { fontSize: 10, fontFamily: 'var(--font-mono, monospace)' },
      labelBgPadding: [4, 2] as [number, number],
      labelBgStyle: { fill: 'var(--card)' },
    };
    for (const link of chainLinks(model))
      edges.push({
        id: `${link.source}->${link.target}`,
        source: link.source,
        target: link.target,
        sourceHandle: 'out',
        targetHandle: 'in',
        type: 'chain',
        animated: live[link.target]?.status === 'running',
        markerEnd: { type: MarkerType.ArrowClosed },
        deletable: editable && link.target !== CANVAS_OUTPUT,
        style,
      });
    for (const entry of model.graph) {
      const labels =
        entry.type === 'conditional'
          ? [
              ...entry.branches.map((b) => conditionLabel(b.condition, 26)),
              ...(entry.fallback ? ['otherwise'] : []),
            ]
          : [];
      containerChildren(entry).forEach((child, i) => {
        edges.push({
          id: `${entry.id}=>${child.id}`,
          source: entry.id,
          target: child.id,
          sourceHandle: 'children',
          targetHandle: 'in',
          type: 'smoothstep',
          selectable: false,
          deletable: false,
          animated: live[child.id]?.status === 'running',
          markerEnd: { type: MarkerType.ArrowClosed },
          style: { ...style, strokeDasharray: '4 3' },
          ...(labels[i] ? { label: labels[i], ...labelProps } : {}),
        });
        if (entry.type === 'loop' || entry.type === 'foreach')
          edges.push({
            id: `${child.id}~>${entry.id}`,
            source: child.id,
            target: entry.id,
            sourceHandle: 'back',
            targetHandle: 'loop-in',
            type: 'smoothstep',
            selectable: false,
            deletable: false,
            label: entry.type === 'loop' ? 'repeat' : 'next item',
            ...labelProps,
            animated: live[entry.id]?.status === 'running',
            style: { ...style, strokeDasharray: '4 3' },
          });
      });
    }
    return { nodes, edges };
  }, [model, positions, parents, live, selected, editable, inputs, hasOutput]);

  // Controlled nodes: React Flow's measurements are kept between rebuilds.
  const [nodes, setNodes] = useState<Node<NodeData>[]>(built.nodes);
  useEffect(() => {
    setNodes((prev) => {
      const old = new Map(prev.map((n) => [n.id, n]));
      return built.nodes.map((n) => {
        const o = old.get(n.id);
        return o?.measured ? { ...n, measured: o.measured } : n;
      });
    });
  }, [built.nodes]);

  const onNodesChange = useCallback(
    (changes: NodeChange<Node<NodeData>>[]) => {
      setNodes((nds) => applyNodeChanges(changes, nds));
      const moved = new Map<string, XYPosition>();
      for (const c of changes) {
        if (c.type === 'position' && c.position) moved.set(c.id, c.position);
        if (c.type === 'select' && c.selected) onSelect(c.id);
      }
      if (moved.size === 0) return;
      setPositions((prev) => {
        const next = { ...prev };
        for (const [id, p] of moved) {
          const before = nodes.find((n) => n.id === id)?.position;
          next[id] = p;
          // Children that were dragged on their own travel with their container.
          if (!before) continue;
          for (const [child, parent] of parents)
            if (parent === id && next[child] && !moved.has(child))
              next[child] = {
                x: next[child].x + (p.x - before.x),
                y: next[child].y + (p.y - before.y),
              };
        }
        return next;
      });
    },
    [nodes, onSelect, parents, setPositions],
  );

  const fail = (message: string) => toast.error(message);
  const update = (next: CanvasModel) => onChange?.(next);

  const actions: CanvasActions = {
    editable,
    insert: (source, type) => {
      if (!onCreate) return;
      const entry = onCreate(type);
      update(insertAfter(model, source, entry));
      onSelect(entry.id);
    },
    unlink: (source, target) => update(disconnect(model, source, target)),
    remove: (id) => {
      const parent = parents.get(id);
      if (parent) {
        const container = model.graph.find((e) => e.id === parent)!;
        const next = removeChild(container, id);
        if ('error' in next) return fail(next.error);
        update({ ...model, graph: model.graph.map((e) => (e.id === parent ? next : e)) });
        onSelect(parent);
        return;
      }
      update(removeEntry(model, id));
      onSelect(CANVAS_INPUT);
    },
    duplicate: (id) => {
      const entry = model.graph.find((e) => e.id === id);
      if (!entry) return;
      const taken = new Set(
        model.graph.flatMap((e) => [e.id, ...containerChildren(e).map((c) => c.id)]),
      );
      const copy = duplicateEntry(entry, taken);
      const p = nodes.find((n) => n.id === id)?.position;
      if (p) setPositions((prev) => ({ ...prev, [copy.id]: { x: p.x + 40, y: p.y + 140 } }));
      update(insertAfter(model, id, copy));
      onSelect(copy.id);
    },
  };

  const isValidConnection: IsValidConnection = (c) =>
    c.source !== c.target &&
    c.targetHandle === 'in' &&
    !parents.has(c.target) &&
    c.target !== CANVAS_INPUT;

  const onConnect = (c: Connection) => {
    const result = connect(model, c.source, c.target);
    if ('error' in result) return fail(result.error);
    update(result.model);
  };

  const onConnectEnd: OnConnectEnd = (event, state) => {
    if (!editable || state.isValid || !state.fromNode || state.fromHandle?.type !== 'source')
      return;
    if (state.fromNode.id === CANVAS_OUTPUT || parents.has(state.fromNode.id)) return;
    const point = 'changedTouches' in event ? event.changedTouches[0]! : event;
    const screen = { x: point.clientX, y: point.clientY };
    const flow = flowRef.current?.screenToFlowPosition(screen) ?? screen;
    setDrop({ source: state.fromNode.id, screen, flow });
  };

  return (
    <ActionsContext.Provider value={actions}>
      <div
        ref={box}
        className={cn('h-[34rem] rounded-lg border', className)}
        data-testid="workflow-graph"
      >
        <ReactFlow
          colorMode={colorMode}
          defaultMarkerColor="var(--muted-foreground)"
          nodes={nodes}
          edges={built.edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onInit={(instance) => {
            flowRef.current = instance;
          }}
          onNodesChange={onNodesChange}
          onNodeClick={(_, node) => onSelect(node.id)}
          onNodeDragStop={() =>
            setPositions((p) => {
              save(p);
              return p;
            })
          }
          onConnect={onConnect}
          onConnectEnd={onConnectEnd}
          isValidConnection={isValidConnection}
          onBeforeDelete={async ({ nodes: gone, edges: cut }) => {
            if (!editable) return false;
            if (gone.length > 0) for (const n of gone) actions.remove(n.id);
            else for (const e of cut) if (e.type === 'chain') actions.unlink(e.source, e.target);
            return false;
          }}
          deleteKeyCode={editable ? ['Backspace', 'Delete'] : null}
          nodesDraggable
          nodesConnectable={editable}
          elementsSelectable
          connectionRadius={36}
          minZoom={0.2}
          maxZoom={1.5}
          panOnScroll
          proOptions={{ hideAttribution: true }}
        >
          <AutoFit fitKey={`${workflowId}:${fitKey}`} size={size} />
          <Background />
          <Controls showInteractive={false} />
          <MiniMap
            pannable
            zoomable
            className="!bg-card hidden lg:block"
            style={{ width: 140, height: 90 }}
            maskColor="color-mix(in oklch, var(--muted), transparent 30%)"
          />
          <Panel position="top-right" className="flex items-center gap-2">
            {editable && (
              <span className="text-muted-foreground bg-card/80 hidden rounded-md px-2 py-1 text-[11px] backdrop-blur lg:inline">
                Drag to move · drag from ● to link · Delete removes
              </span>
            )}
            <Button
              size="sm"
              variant="outline"
              className="bg-card h-7"
              onClick={() => {
                setPositions({});
                save({});
              }}
            >
              <LayoutGrid aria-hidden /> Tidy up
            </Button>
          </Panel>
        </ReactFlow>
        <DropPicker
          at={drop?.screen ?? null}
          onClose={() => setDrop(null)}
          onPick={(type) => {
            if (!drop || !onCreate) return;
            const entry = onCreate(type);
            setPositions((prev) => {
              const next = { ...prev, [entry.id]: { x: drop.flow.x, y: drop.flow.y - 27 } };
              save(next);
              return next;
            });
            update(insertAfter(model, drop.source, entry));
            onSelect(entry.id);
            setDrop(null);
          }}
        />
      </div>
    </ActionsContext.Provider>
  );
}
